import { useEffect, useId, useRef, useState } from "react";

import { useLazyQuery } from "@apollo/client";
import Swiper from "swiper";
import { theme } from "twin.macro";
import { A11y, Autoplay, Navigation } from "swiper/modules";
import { GET_BANNERS_V2 } from "@/graphql/banners";
import { GET_HUBS } from "@/graphql/hubs";
import Analytics from "@/utils/Analytics";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import HubsSection from "../HubsSection";
import SwiperNavButtons from "../SwiperNavButtons";
import Skeleton from "@/components/Atoms/Skeleton";
import type { IBanner, IBannersData } from "@/types/banners";

import {
  StyledButtonPrevContainer,
  StyledSwiperSlide,
  StyledWrapperRoot,
  StyledWrapperSwiperBanner,
  StyledBanner,
  StyledWrapperGradient,
  StyledButtonNextContainer,
  StyledSkeletonBanner,
  StyledBannerImage,
} from "./styles";
import { pageView } from "@/utils/analyticsV2";

interface IBannersProps {
  storeId?: number;
}

const Banners = ({ storeId }: IBannersProps): React.ReactElement | null => {
  const id = useId();
  const [banners, setBanners] = useState<IBanner[]>([]);
  const [loadingHubs, setLoadingHubs] = useState(true);
  const [imageLoaded, setImageLoaded] = useState<Record<number, boolean>>({});
  const [index, setIndex] = useState(0);
  const swiperRef = useRef<Swiper | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const swiperClassName = `swiper-container-${id.replace(/:/g, "")}`;
  const swiperNavigationClassName = `swiper-navigation-${id.replace(/:/g, "")}`;

  const isMedium = useMediaQuery("(min-width: {medium})");
  const isButtonSwiperOutside = useMediaQuery("( min-width: 1410px )");
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const handleEventAnalytic = async (data) => {
    await Analytics.BannerClick(data);
    await pageView.eventClickButton();
  };

  const [getBanners, { loading: isFetchingBanners, error }] = useLazyQuery<IBannersData>(GET_BANNERS_V2, {
    onCompleted: (data: IBannersData) => {
      setImageLoaded({});
      setBanners(data?.storeBanners ?? []);
    },
    onError: () => {
      setImageLoaded({});
      setBanners([]);
    },
  });

  const [getHubs, { loading: isFetchingHubs, data: hubsData = [], error: errorHubs }] = useLazyQuery(
    GET_HUBS,
    {
      variables: { storeId: String(storeId) },
      onCompleted: () => {
        setLoadingHubs(false);
      },
      onError: () => {
        setLoadingHubs(false);
      },
    }
  );

  useEffect(() => {
    if (storeId) {
      // Only fetch banners if storeId is available
      void getBanners({ variables: { storeId: String(storeId) } });
    }
  }, [storeId]);

  useEffect(() => {
    if (storeId) {
      void getHubs();
    }
  }, [storeId]);

  useEffect(() => {
    if (banners?.length > 0) {
      swiperRef.current = new Swiper(`.${swiperClassName}`, {
        modules: [Navigation, Autoplay, A11y],
        navigation: {
          nextEl: `.${swiperNavigationClassName}-next`,
          prevEl: `.${swiperNavigationClassName}-prev`,
          hiddenClass: "swiper-navigation-hidden",
          disabledClass: "swiper-navigation-disabled",
        },
        spaceBetween: 10,
        grabCursor: banners?.length > 1,
        slidesPerView: 1.2,
        slidesPerGroup: 1,
        centeredSlides: true,
        loop: banners?.length > 1,
        breakpoints: {
          [theme`screens.medium`.replace("px", "")]: {
            slidesPerView: 1,
          },
        },
      });
    }

    return () => {
      swiperRef.current?.destroy();
    };
  }, [JSON.stringify(banners), isButtonSwiperOutside]);

  useEffect(() => {
    if (swiperRef.current && storeId) {
      const handleChange = (swiper: Swiper) => {
        setIndex(swiper.realIndex);
        if (swiper.realIndex <= banners?.length) {
          containerRef?.current?.style.setProperty(
            "--myColor1",
            banners?.[swiper.realIndex]?.backgroundColor || "transparent"
          );
        }
      };

      swiperRef.current?.on("slideChange", handleChange);

      // Start loop with the first banner
      swiperRef.current?.slideToLoop(0, 0);

      return () => {
        swiperRef.current?.off("slideChange", handleChange);
      };
    }
  }, [JSON.stringify(banners), storeId, isButtonSwiperOutside]);

  useEffect(() => {
    if (swiperRef.current) {
      if (imageLoaded?.[index]) {
        swiperRef.current.autoplay.start();
      } else {
        swiperRef.current.autoplay.stop();
      }
    }
  }, [JSON.stringify(imageLoaded), index]);

  return (
    <StyledWrapperGradient ref={containerRef}>
      {!isFetchingBanners ? (
        <>
          {!error && banners && banners?.length > 0 && (
            <StyledWrapperRoot>
              <StyledWrapperSwiperBanner className={`swiper ${swiperClassName}`}>
                <div className="swiper-wrapper">
                  {banners.map((banner, i) => {
                    return (
                      <StyledSwiperSlide
                        className="swiper-slide"
                        data-swiper-autoplay="10000"
                        key={`${banner.pk}-${i}`}
                      >
                        <StyledBanner
                          href={banner.title ? `/promo/${banner.pk}` : banner.target}
                          onClick={() => {
                            void handleEventAnalytic(banner);
                          }}
                        >
                          <StyledBannerImage
                            src={!isMedium ? banner?.imageMobile?.url : banner?.image?.url}
                            alt={!isMedium ? banner?.imageMobile?.alt : banner?.image?.alt}
                            loading="lazy"
                            effect="blur"
                            onLoad={() => {
                              setImageLoaded({
                                ...imageLoaded,
                                [index]: true,
                              });
                            }}
                          />
                        </StyledBanner>

                        <div
                          className="swiper-lazy-preloader swiper-lazy-preloader-white"
                          tw="(absolute left-[50%] -translate-x-1/2)!"
                        />
                      </StyledSwiperSlide>
                    );
                  })}
                </div>
              </StyledWrapperSwiperBanner>
              {/* Buttons swiper inside the image   */}
              {banners.length > 1 && !isButtonSwiperOutside && (
                <>
                  <StyledButtonPrevContainer>
                    <SwiperNavButtons
                      isCircular
                      type="prev"
                      buttonProps={{
                        className: `${swiperNavigationClassName}-prev swipperNavButtonBanners`,
                      }}
                    />
                  </StyledButtonPrevContainer>

                  <StyledButtonNextContainer>
                    <SwiperNavButtons
                      isCircular
                      type="next"
                      buttonProps={{
                        className: `${swiperNavigationClassName}-next swipperNavButtonBanners`,
                      }}
                    />
                  </StyledButtonNextContainer>
                </>
              )}
            </StyledWrapperRoot>
          )}
        </>
      ) : (
        <StyledWrapperRoot>
          <div tw="md:(w-full mx-auto) relative">
            {/* <BannerLoader isMobile={!isMedium} /> */}

            <StyledSkeletonBanner>
              <Skeleton type="gallery" height="100%" withBorder={false} animate />
            </StyledSkeletonBanner>
          </div>
        </StyledWrapperRoot>
      )}

      {!errorHubs && (
        <HubsSection
          isLoading={loadingHubs || isFetchingHubs}
          hubs={hubsData?.hubs?.edges?.map((item) => item?.node) || []}
        />
      )}
      {/* Buttons swiper outside the image   */}
      {banners.length > 1 && isButtonSwiperOutside && (
        <>
          <StyledButtonPrevContainer>
            <SwiperNavButtons
              type="prev"
              buttonProps={{
                className: `${swiperNavigationClassName}-prev swipperNavButtonBanners`,
              }}
            />
          </StyledButtonPrevContainer>

          <StyledButtonNextContainer>
            <SwiperNavButtons
              type="next"
              buttonProps={{
                className: `${swiperNavigationClassName}-next swipperNavButtonBanners`,
              }}
            />
          </StyledButtonNextContainer>
        </>
      )}
    </StyledWrapperGradient>
  );
};

export default Banners;
